// ============================================================================
// SET-OFF SERVICES (ADR-0051, ADR-0060) — entity + BU scoped.
// One implementation over the two party ledgers: Supplier Set-off (AP) and
// Customer Set-off (AR) differ only in their endpoints.
// ============================================================================

import { get, post } from '../client/axiosClient';
import { supplierSetoffEndpoints, customerSetoffEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, ID,
  SetoffDto, SetoffCandidatesDto, CreateSetoffRequest, SetoffQueryParams, MarkDeductionRequest, DeductionReasonDto,
} from '../types';

type SetoffEndpoints = typeof supplierSetoffEndpoints | typeof customerSetoffEndpoints;

export class PartySetoffService {
  constructor(private readonly endpoints: SetoffEndpoints) {}

  async getAll(params?: SetoffQueryParams): Promise<PaginatedResponse<SetoffDto>> {
    const endpoint = params ? buildEndpointWithQuery(this.endpoints.list(), params) : this.endpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<SetoffDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getById(id: ID): Promise<SetoffDto> {
    const response = await get<ApiResponse<SetoffDto>>(this.endpoints.getById(id));
    return response.data.data;
  }
  /** The party's open items on both sides of its ledger. */
  async candidates(partyId: ID): Promise<SetoffCandidatesDto> {
    const response = await get<ApiResponse<SetoffCandidatesDto>>(buildEndpointWithQuery(this.endpoints.candidates(), { partyId }));
    return response.data.data;
  }
  async create(data: CreateSetoffRequest): Promise<SetoffDto> {
    const response = await post<ApiResponse<SetoffDto>>(this.endpoints.create(), data);
    return response.data.data;
  }
  /** ADR-0060: Mark short-paid (customer) / Mark withheld (supplier) — bills close, the amounts stay open as deduction items. */
  async markDeduction(data: MarkDeductionRequest): Promise<SetoffDto> {
    const response = await post<ApiResponse<SetoffDto>>(this.endpoints.markDeduction(), data);
    return response.data.data;
  }
  async deductionReasons(): Promise<DeductionReasonDto[]> {
    const response = await get<ApiResponse<DeductionReasonDto[]>>(this.endpoints.deductionReasons());
    return Array.from(response.data.data || []);
  }
  async cancel(id: ID, rowVersion: number): Promise<void> {
    await post<ApiResponse<unknown>>(buildEndpointWithQuery(this.endpoints.cancel(id), { rowVersion }), {});
  }
}

export const supplierSetoffService = new PartySetoffService(supplierSetoffEndpoints);
export const customerSetoffService = new PartySetoffService(customerSetoffEndpoints);
export default supplierSetoffService;
